/* eslint-disable react/display-name */
import * as React from 'react'
import * as z from 'zod'

import {
  type CustomCell,
  type CustomRenderer,
  GridCellKind,
  getMiddleCenterBias,
} from '@glideapps/glide-data-grid'
import { drawImage } from '../../draw-fns'
import {
  actionSchema,
  drawAssetActions,
  getSquareBB,
  isOverAction,
  pointIsWithinBB,
} from '../utils'
import { getMediaTypeByFileExtension } from '../../../../../media/utils'

const mediaPreviewCellSchema = z.object({
  kind: z.literal('media-preview-cell'),
  value: z.string(),
  rounding: z.number().optional(),
  event: z.any().optional(),
  duration: z.string().optional(),
  actions: z.array(actionSchema),
})

export type MediaPreviewCellProps = z.infer<typeof mediaPreviewCellSchema> & {
  readonly event?: React.ChangeEvent<HTMLInputElement>
}

export type MediaPreviewCell = CustomCell<MediaPreviewCellProps>

export const MediaPreviewRenderer: CustomRenderer<MediaPreviewCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is MediaPreviewCell => {
    const kind = 'kind' in cell.data && cell.data.kind
    return kind === 'media-preview-cell'
  },
  draw: (args) => {
    const { rect, cell, theme, hoverX, hoverY, highlighted, ctx } = args
    const fileName =
      cell.data.value.split('/').pop()?.replace(/\?.+$/, '') ?? 'Untitled file'
    const mediaType = getMediaTypeByFileExtension(fileName)
    if (mediaType === 'image') {
      drawImage(
        args,
        [cell.data.value],
        cell.data.rounding ?? theme.roundingRadius ?? 6,
        cell.contentAlign
      )
    } else {
      const xPad = theme.cellHorizontalPadding
      const drawX = rect.x + xPad

      ctx.font = theme.baseFontFull
      ctx.fillStyle = theme.textDark
      ctx.fillText(
        fileName,
        drawX,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
      )
    }

    const bb = getSquareBB(
      rect.x,
      Math.floor(rect.y + rect.height / 2),
      rect.width * 2
    )
    const hovered =
      hoverX && hoverY
        ? pointIsWithinBB(rect.x + hoverX, rect.y + hoverY, bb)
        : false

    if (!hovered && !highlighted) return true

    drawAssetActions(args)
    return true
  },
  onClick: (e) => {
    const clickedAction = isOverAction(e)

    if (e.isDoubleClick) return undefined

    if (!clickedAction) {
      e.preventDefault()
      return undefined
    }

    const action = e.cell.data.actions.find((a) => a.name === clickedAction)
    if (action?.onClick) {
      e.preventDefault()
      action.onClick(e.cell.data.value, () => {})
    }

    return undefined
  },
  needsHover: true,
  needsHoverPosition: true,
  // provideEditor: () => ({ editor: MediaPreview }),
}
